import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { DetailHeader } from '../common/DetailHeader';
import { FormContainer } from '../common/form/FormContainer';
import { Input } from '../common/form/Input';
import { getSupplierEmployee, selectSupplierEmployee, selectSupplierEmployeeState } from '../../store/entities/supplierEmployee';
import { PhoneInput } from '../common/form/PhoneInput';
import { LanguageSelect } from '../common/form/LanguageSelect';
import { SUPPORTED_LANGUAGES } from '../../utils/i18n';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { SupplierEmployeeDto } from '@api/models/suppliers/SupplierEmployeeDto';
import { resources } from '@api/resources';
import { ShowResourceContent } from '../ShowResourceContent';
import { doNothing } from '../../utils/shared';
import { SupplierEmployeePinReveal } from '../common/SupplierEmployeePinReveal';
import { supplierEmployeeService } from '@api/services/services';
import { useNavigation } from '../../utils/urlPaths';
import { InvitationModal } from './InvitationModal';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import { ShowImageButton } from '../invitation/Detail/Tabs/ShowImageButton';
import { SubmitButtonsContainer } from '../common/form/SubmitButtonsContainer';
import { useCreateSupplierEmployee } from './useCreateSupplierEmployee';
import { selectIdentity } from '../../store/entities/identity';
import { hasRole } from '../../utils/roles';
import { UserRole } from '@api/models/enums/UserRole';

export const InvitationTypeVisitDetail: FC = () => {
  const { t } = useTranslation();
  const { supplierEmployeeId } = useParams();
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const supplierEmployee = useAppSelector(selectSupplierEmployee);
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const createSupplierEmployee = useCreateSupplierEmployee(navigate);
  const [open, setOpen] = useState(false);
  const identity = useAppSelector(selectIdentity);

  const canAssignToSupplier = hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah, UserRole.ManagerWorkplace]);
  const canViewPinAndTrainings = hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist]);

  useEffect(() => {
    dispatch(getSupplierEmployee(supplierEmployeeId!));
  }, [supplierEmployeeId, dispatch]);

  return (
    <>
      <InvitationModal
        open={open}
        onClose={() => setOpen(false)}
        onSupplierSelectClick={(supplier) => {
          createSupplierEmployee(supplier, supplierEmployee.id!, supplierEmployee);
        }}
      />
      <DetailHeader title="invitationTypeVisit.detailTitle" backTo="/visit">
        {canAssignToSupplier && (
          <QcsLoadingButton variant="outlined" onClick={() => setOpen(true)}>
            {t('invitationTypeVisit.assignToSupplier')}
          </QcsLoadingButton>
        )}
      </DetailHeader>
      <ShowResourceContent resource={resources.getSupplierEmployee} fetchStateSelector={selectSupplierEmployeeState}>
        <FormikDebounce<SupplierEmployeeDto> initialValues={supplierEmployee} onSubmit={doNothing}>
          <Form>
            <FormContainer>
              <Input name="name" label={t('invitationTypeVisit.name')} maxLength={100} required={true} disabled={true} />
              <Input name="lastName" label={t('invitationTypeVisit.lastName')} maxLength={100} required={true} disabled={true} />
              <Input name="supplier.name" label={t('invitationTypeVisit.company')} disabled={true} />
              <Input name="email" removeSpaces label={t('invitationTypeVisit.email')} maxLength={100} disabled={true} />
              <PhoneInput name="phone" label={t('invitationTypeVisit.phone')} disabled={true} />
              <LanguageSelect
                name="language"
                label={t('invitationTypeVisit.language')}
                languages={SUPPORTED_LANGUAGES}
                allowedLanguages={companyCustomization?.applicationLanguages}
                required={true}
                disabled={true}
              />

              {canViewPinAndTrainings && <SupplierEmployeePinReveal supplierEmployeeId={supplierEmployeeId!} />}
              {canViewPinAndTrainings && (
                <SubmitButtonsContainer>
                  <ShowImageButton
                    documentUrl=""
                    variant="outlined"
                    fetchDocument={async () => {
                      const res = await supplierEmployeeService.getSupplierEmployeeProtocol(supplierEmployeeId!, { responseType: 'blob' });
                      return {
                        data: res.data as Blob,
                        headers: { 'content-type': 'application/pdf' },
                      };
                    }}
                  >
                    {t('supplierEmployee.showProtocol')}
                  </ShowImageButton>
                </SubmitButtonsContainer>
              )}
            </FormContainer>
          </Form>
        </FormikDebounce>
      </ShowResourceContent>
    </>
  );
};
